import React, { useState } from 'react';
import { useJWTAuth } from '../context/JWTAuthContext';
import { useJWTApiService } from '../utils/jwtApiService';

const ProfilePage = () => {
  const {
    user,
    isAuthenticated,
    logout,
    apiCall
  } = useJWTAuth();

  const jwtApiService = useJWTApiService(apiCall);
  
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    nickname: user?.nickname || '',
    email: user?.email || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);

  // 로그인하지 않은 경우 로그인 안내
  if (!isAuthenticated) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>🔐 로그인이 필요합니다</h2>
          <p>프로필을 보려면 먼저 로그인해주세요.</p>
        </div>
      </div>
    );
  }

  // 프로필 수정 처리
  const handleProfileUpdate = async () => {
    if (!editData.nickname.trim() || !editData.email.trim()) {
      setMessage({ type: 'error', text: '모든 필드를 입력해주세요.' });
      return;
    }

    setIsLoading(true);
    try {
      const result = await jwtApiService.updateProfile(editData);
      if (result.success) {
        setMessage({ type: 'success', text: '프로필이 성공적으로 수정되었습니다.' });
        setEditMode(false);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '프로필 수정 중 오류가 발생했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 변경 처리
  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage({ type: 'error', text: '모든 필드를 입력해주세요.' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: '새 비밀번호가 일치하지 않습니다.' });
      return;
    }

    // 비밀번호 유효성 검사 (간단한 버전)
    const validation = {
      isValid: passwordData.newPassword.length >= 8,
      message: passwordData.newPassword.length >= 8 ? '' : '비밀번호는 8자 이상이어야 합니다.'
    };
    if (!validation.isValid) {
      setMessage({ type: 'error', text: validation.message });
      return;
    }

    setIsLoading(true);
    try {
      const result = await jwtApiService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      if (result.success) {
        setMessage({ type: 'success', text: '비밀번호가 성공적으로 변경되었습니다.' });
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '비밀번호 변경 중 오류가 발생했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  // 회원탈퇴 처리
  const handleAccountDelete = async () => {
    if (!window.confirm('정말로 회원탈퇴를 하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    setIsLoading(true);
    try {
      const result = await jwtApiService.deleteAccount();
      if (result.success) {
        alert('회원탈퇴가 완료되었습니다. 감사했습니다.');
        // 탈퇴 후 홈페이지로 이동하거나 로그아웃 처리는 AuthContext에서 처리됨
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '회원탈퇴 중 오류가 발생했습니다.' });
    } finally {
      setIsLoading(false);
    }
  };

  // 사용자 등급에 따른 색상 반환
  const getGradeColor = (grade) => {
    const colors = {
      'newbie': '#95a5a6',
      'bronze': '#d35400', 
      'silver': '#7f8c8d',
      'gold': '#f1c40f',
      'platinum': '#9b59b6',
      'diamond': '#3498db'
    };
    return colors[grade] || '#95a5a6';
  };

  // 가입일로부터 경과일 계산
  const getDaysSinceJoined = () => {
    if (!user?.created_at) return 0;
    const joinDate = new Date(user.created_at);
    const today = new Date();
    const diffTime = Math.abs(today - joinDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="container">
      {/* 메시지 표시 */}
      {message.text && (
        <div className="card" style={{
          padding: '12px 16px',
          marginBottom: '16px',
          background: message.type === 'success' ? '#d5edda' : '#f8d7da',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          color: message.type === 'success' ? '#155724' : '#721c24'
        }}>
          {message.text}
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="card">
        <div style={{ 
          display: 'flex', 
          gap: '8px',
          borderBottom: '1px solid #dee2e6',
          marginBottom: '20px',
          padding: '0 0 16px 0'
        }}>
          {[
            { id: 'profile', label: '👤 프로필', icon: '👤' },
            { id: 'edit', label: '✏️ 정보수정', icon: '✏️' },
            { id: 'password', label: '🔒 비밀번호', icon: '🔒' },
            { id: 'account', label: '⚙️ 계정설정', icon: '⚙️' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMessage({ type: '', text: '' });
              }}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '6px',
                background: activeTab === tab.id ? '#3498db' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#666',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === tab.id ? '500' : 'normal'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 프로필 보기 탭 */}
        {activeTab === 'profile' && (
          <div>
            {/* 프로필 헤더 */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '20px',
              flexWrap: 'wrap',
              marginBottom: '30px'
            }}>
              <div style={{ 
                fontSize: '48px',
                background: '#ecf0f1',
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                👤
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h2 style={{ margin: '0 0 8px 0', color: '#2c3e50' }}>
                  {user.nickname}
                </h2>
                <p style={{ margin: '0 0 4px 0', color: '#666' }}>
                  {user.email}
                </p>
                <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>
                  ID: {user.user_id}
                </p>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '12px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    background: getGradeColor(user.grade),
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {user.grade?.toUpperCase() || 'NEWBIE'}
                  </span>
                  <span style={{
                    background: user.is_active ? '#27ae60' : '#e74c3c',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {user.is_active ? '활성' : '비활성'}
                  </span>
                </div>
              </div>
            </div>

            {/* 활동 통계 */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ 
                textAlign: 'center', 
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>📅</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  color: '#2c3e50',
                  marginBottom: '4px'
                }}>
                  {getDaysSinceJoined()}일
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  가입일
                </div>
              </div>
              
              <div style={{ 
                textAlign: 'center', 
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>📝</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  color: '#2c3e50',
                  marginBottom: '4px'
                }}>
                  0
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  작성 게시글
                </div>
              </div>

              <div style={{ 
                textAlign: 'center', 
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>💬</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  color: '#2c3e50',
                  marginBottom: '4px'
                }}>
                  0
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  작성 댓글
                </div>
              </div>

              <div style={{ 
                textAlign: 'center', 
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ fontSize: '20px', marginBottom: '8px' }}>❤️</div>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  color: '#2c3e50',
                  marginBottom: '4px'
                }}>
                  0
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  받은 좋아요
                </div>
              </div>
            </div>

            {/* 가입 정보 */}
            <div style={{ 
              background: '#f8f9fa',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h3 style={{ margin: '0 0 12px 0', color: '#2c3e50' }}>가입 정보</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#7f8c8d' }}>가입일:</span>
                  <span>{user.created_at ? new Date(user.created_at).toLocaleDateString('ko-KR') : '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#7f8c8d' }}>등급:</span>
                  <span>{user.grade?.toUpperCase() || 'NEWBIE'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#7f8c8d' }}>상태:</span>
                  <span>{user.is_active ? '활성' : '비활성'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 정보수정 탭 */}
        {activeTab === 'edit' && (
          <div>
            <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>프로필 정보 수정</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                  사용자 ID
                </label>
                <input
                  type="text"
                  value={user.user_id}
                  disabled
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: '#f8f9fa',
                    color: '#666'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>사용자 ID는 변경할 수 없습니다.</small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                  닉네임
                </label>
                <input
                  type="text"
                  value={editData.nickname}
                  onChange={(e) => setEditData(prev => ({ ...prev, nickname: e.target.value }))}
                  placeholder="닉네임을 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                  이메일
                </label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="이메일을 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleProfileUpdate}
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    background: '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  {isLoading ? '수정 중...' : '저장'}
                </button>
                <button
                  onClick={() => {
                    setEditData({
                      nickname: user?.nickname || '',
                      email: user?.email || ''
                    });
                    setMessage({ type: '', text: '' });
                  }}
                  disabled={isLoading}
                  style={{
                    padding: '10px 20px',
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 비밀번호 변경 탭 */}
        {activeTab === 'password' && (
          <div>
            <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>비밀번호 변경</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                  현재 비밀번호
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder="현재 비밀번호를 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                  새 비밀번호
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder="새 비밀번호를 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  대소문자, 숫자, 특수문자를 포함하여 12자리 이상
                </small>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                  새 비밀번호 확인
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder="새 비밀번호를 다시 입력하세요"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <button
                onClick={handlePasswordChange}
                disabled={isLoading}
                style={{
                  padding: '10px 20px',
                  background: '#e67e22',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1,
                  alignSelf: 'flex-start'
                }}
              >
                {isLoading ? '변경 중...' : '비밀번호 변경'}
              </button>
            </div>
          </div>
        )}

        {/* 계정설정 탭 */}
        {activeTab === 'account' && (
          <div>
            <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>계정 설정</h3>
            
            {/* 로그아웃 */}
            <div style={{ 
              padding: '16px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
              marginBottom: '16px'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#2c3e50' }}>로그아웃</h4>
              <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
                현재 계정에서 로그아웃합니다.
              </p>
              <button
                onClick={logout}
                style={{
                  padding: '8px 16px',
                  background: '#f39c12',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                로그아웃
              </button>
            </div>

            {/* 회원탈퇴 */}
            <div style={{ 
              padding: '16px',
              background: '#fdedec',
              borderRadius: '8px',
              border: '1px solid #f5b7b1'
            }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#e74c3c' }}>회원 탈퇴</h4>
              <p style={{ margin: '0 0 12px 0', color: '#666', fontSize: '14px' }}>
                계정을 영구적으로 삭제합니다. 이 작업은 되돌릴 수 없습니다.<br/>
                모든 게시글, 댓글, 개인정보가 삭제됩니다.
              </p>
              <button
                onClick={handleAccountDelete}
                disabled={isLoading}
                style={{
                  padding: '8px 16px',
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.7 : 1
                }}
              >
                {isLoading ? '탈퇴 처리 중...' : '회원 탈퇴'}
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default ProfilePage;